
import api from ".";
import { getToken } from "../Auth/Token";

export const getSalesOrdersByTag = async (tags) => {
  try {
    const token = getToken();

    let tagPayload;

    if (Array.isArray(tags)) {
      const cleaned = tags
        .map(t => String(t).trim())
        .filter(t => t && t.startsWith("#"));
      
      if (cleaned.length === 0) {
        throw new Error("No valid tags provided");
      }
      tagPayload = cleaned.length === 1 ? cleaned[0] : cleaned;
    } else {
      const singleTag = String(tags || "").trim();
      if (!singleTag || !singleTag.startsWith("#")) {
        throw new Error("Invalid or missing tag. Must start with # (e.g. #89240)");
      }
      tagPayload = singleTag;
    }

    const res = await api.post(
      "/api/erpag/salesorder-by-tag",
      { tag: tagPayload },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const { tag, count, results } = res.data;
    let parsedResults = results || [];

    if (parsedResults.length > 0 && typeof parsedResults[0] === "string") {
      try {
        parsedResults = parsedResults.map(item => JSON.parse(item));
      } catch (e) {
        console.warn("Some ERPAG items were stringified but failed to parse", e);
      }
    }

    return {
      success: true,
      tag: tag || tagPayload,
      count: parsedResults.length,
      data: parsedResults, // always an array
    };

  } catch (error) {
    console.error("getSalesOrdersByTag Error:", error.response?.data || error.message);

    const errorMessage = 
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Unknown error";

    return {
      success: false,
      error: errorMessage,
      data: [],
      count: 0,
    };
  }
};

export const submitXmlToAceLynk = async (xml) => {
  try {
    const response = await api.post(
      `/api/erpag/submit-to-acelynk`,
      { xml },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.details ||
        error.response?.data?.error ||
        error.message,
    };
  }
};

export const fetchErpagCategories = async () => {
  const MAX_RETRIES = 3;
  let lastError = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await api.get("/api/erpag/fetch-category");
      const data = Array.isArray(response.data?.data) ? response.data.data : [];

      return {
        success: Boolean(response.data?.success),
        data,
      };
    } catch (error) {
      lastError = error;
      const is429 = error.response?.status === 429;

      // If it's a 429 error and we haven't reached max retries, wait and try again
      if (is429 && attempt < MAX_RETRIES) {
        const delay = attempt * 1000; // 1s, 2s... delay
        console.warn(`Rate limited (429). Retry attempt ${attempt} in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // If it's a different error or we've exhausted retries, break the loop and handle it below
      break;
    }
  }

  // Handle final error after all attempts (or non-429 error)
  console.error("fetchErpagCategories Error:", lastError.response?.data || lastError.message);

  return {
    success: false,
    error:
      lastError.response?.data?.error ||
      lastError.response?.data?.message ||
      lastError.message,
    data: [],
  };
};

export const fetchErpagWarehouses = async () => {
  const MAX_RETRIES = 3;
  let lastError = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await api.get("/api/erpag/warehouse-allocation");
      const data = Array.isArray(response.data?.data) ? response.data.data : [];

      return {
        success: Boolean(response.data?.success),
        data,
      };
    } catch (error) {
      lastError = error;
      const is429 = error.response?.status === 429;

      // If it's a 429 error and we haven't reached max retries, wait and try again
      if (is429 && attempt < MAX_RETRIES) {
        const delay = attempt * 1000; // 1s, 2s... delay
        // console.warn(`Rate limited (429). Retry attempt ${attempt} in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // If it's a different error or we've exhausted retries, break the loop and handle it below
      break;
    }
  }

  // Handle final error after all attempts (or non-429 error)
  console.error("fetchErpagCategories Error:", lastError.response?.data || lastError.message);

  return {
    success: false,
    error:
      lastError.response?.data?.error ||
      lastError.response?.data?.message ||
      lastError.message,
    data: [],
  };
};
